import { getContractPaymentProperty, PAYMENT_PROPERTY, } from "../../payment/index.js";
import { getSubscriptionProduct } from "../../subscription-product/services/getSubscriptionProduct.js";
import { getProgressionIndex } from "../getMeteredVariables.js";
import { getSubscriptionContract } from "../queries/getSubscriptionContract.core.js";
import { getUsageTracked } from "../queries/getUsageTracked.subscriptions.js";
import { getPaymentInput } from "../selectors/getPaymentInput.js";
import { getMetaValueFromKey } from "@cappelendamm/crystallize-utils";
import { getContractInitialSku } from "../selectors/meta.js";
import { isContractCancelled } from "../utils/isContractCancelled.js";
import { resolveContractStatus } from "./resolveContractStatus.js";
import { SUBSCRIPTION_CONTRACT_META_KEY } from "../constants.js";
import { getSubscriptionKind } from "../delivery/getSubscriptionKind.js";
import { getContractDeliveryInput } from "../delivery/getContractDeliveryInput.js";
import { getLegacyDeliveryFields, resolveDelivery, } from "../delivery/resolveSubscriptionDelivery.js";
const SKIPPED_DISPATCH_PREFIX = "skipped-dispatch-";
const SKIPPED_BOOK_PREFIX = "skipped-book-";
const getSkipDescription = ({ usage, dispatchBatchCode, }) => {
    const sortedUsage = [...usage].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const getDescription = (entry) => {
        const description = entry?.description?.trim();
        return description ? description : null;
    };
    if (dispatchBatchCode) {
        const skippedDispatchKey = `${SKIPPED_DISPATCH_PREFIX}${dispatchBatchCode}`;
        const matchingDispatch = sortedUsage.find(({ idempotencyKey }) => idempotencyKey === skippedDispatchKey);
        const matchingDispatchDescription = getDescription(matchingDispatch);
        if (matchingDispatchDescription) {
            return matchingDispatchDescription;
        }
    }
    const latestSkippedDispatch = sortedUsage.find(({ idempotencyKey }) => idempotencyKey?.startsWith(SKIPPED_DISPATCH_PREFIX));
    const latestSkippedDispatchDescription = getDescription(latestSkippedDispatch);
    if (latestSkippedDispatchDescription) {
        return latestSkippedDispatchDescription;
    }
    const latestSkippedBook = sortedUsage.find(({ idempotencyKey }) => idempotencyKey?.startsWith(SKIPPED_BOOK_PREFIX));
    return getDescription(latestSkippedBook);
};
export const getSubscriptionContractDetails = async ({ contractId }, { client, allowMissingPayment = false }) => {
    const contract = await getSubscriptionContract({ contractId }, { client });
    const renewAt = contract.status?.renewAt;
    const { gifts: giftConfig, ...product } = await getSubscriptionProduct({
        sku: contract.item.sku,
    }, { client });
    const usage = await getUsageTracked({ contractId }, { client });
    const isActive = !isContractCancelled(renewAt);
    // A contract created for the next club (a child's next age group) only has
    // the meta on the contract, not on the item
    const getItemOrContractMeta = (key) => getMetaValueFromKey(contract.item.meta, key) ??
        getMetaValueFromKey(contract.meta, key);
    const saltoContractId = getItemOrContractMeta(SUBSCRIPTION_CONTRACT_META_KEY.SALTO_CONTRACT_ID);
    const source = getItemOrContractMeta(SUBSCRIPTION_CONTRACT_META_KEY.SOURCE);
    const previousContractCreatedAt = getMetaValueFromKey(contract.meta, SUBSCRIPTION_CONTRACT_META_KEY.PREVIOUS_CONTRACT_CREATED_AT);
    const bindingPeriodStartedAt = getMetaValueFromKey(contract.meta, SUBSCRIPTION_CONTRACT_META_KEY.BINDING_PERIOD_STARTED_AT);
    const saltoCreatedAt = source === "SALTO"
        ? getMetaValueFromKey(contract.meta, SUBSCRIPTION_CONTRACT_META_KEY.SALTO_CREATED_AT)
        : null;
    const payment = getPaymentInput(contract, { allowMissingPayment });
    const paymentNewsletterConsent = getContractPaymentProperty(payment.custom, PAYMENT_PROPERTY.CONTRACT.NEWSLETTER_CONSENT);
    const contractPendingStatus = getContractPaymentProperty(payment.custom, PAYMENT_PROPERTY.CONTRACT.CONTRACT_PENDING_STATUS);
    const contractStatus = await resolveContractStatus({
        isActive,
        customerIdentifier: contract.customerIdentifier,
        client,
        paymentCustom: payment.custom,
    });
    const initialSku = getContractInitialSku(contract);
    const lastReceivedBookSku = getProgressionIndex(usage, product.plan)?.idempotencyKey;
    const { periodsToSkip, dispatchBatchCode, holdbackUntil, gifts, receivedGifts, isGiftCampaign, isWithoutCreatedOrders, } = getContractDeliveryInput({
        meta: contract.meta,
        isBookClub: product.isBookClub,
        giftConfig,
        lastReceivedBookSku,
    });
    const skipDescription = getSkipDescription({
        usage,
        dispatchBatchCode,
    });
    const delivery = resolveDelivery({
        kind: getSubscriptionKind(product),
        product,
        contract,
        usage,
        renewAt,
        periodsToSkip,
        dispatchBatchCode: dispatchBatchCode ?? undefined,
        holdbackUntil,
        isGiftCampaign,
        isWithoutCreatedOrders,
        selectedGiftSkus: gifts,
        giftConfig,
    });
    const { nextDispatch, nextBook, nextDispatchProducts, skipBookDeadline } = getLegacyDeliveryFields(delivery);
    const cancelReason = getMetaValueFromKey(contract.meta, SUBSCRIPTION_CONTRACT_META_KEY.CANCEL_REASON);
    const cancelSource = getMetaValueFromKey(contract.meta, SUBSCRIPTION_CONTRACT_META_KEY.CANCEL_SOURCE);
    return {
        ...product,
        isSeries: product.isSeries,
        isBookClubSubscription: product.isBookClub,
        delivery,
        giftConfig,
        endingInvoice: product.endingInvoice,
        ...contract,
        contractId,
        customerId: contract.customerIdentifier,
        createdAt: saltoCreatedAt || contract.createdAt,
        gifts,
        saltoContractId,
        source,
        isActive,
        payment,
        contractStatus,
        paymentNewsletterConsent,
        contractPendingStatus,
        isGiftCampaign,
        nextBook,
        initialSku,
        nextDispatch,
        nextDispatchProducts,
        receivedGifts,
        lastReceivedBookSku,
        periodsToSkip,
        holdbackUntil,
        isWithoutCreatedOrders,
        skipBookDeadline,
        skipDescription,
        status: {
            ...contract.status,
            price: contract.status?.phase?.price,
            currency: contract.status?.phase?.currency,
        },
        cancelReason,
        cancelSource,
        previousContractCreatedAt,
        bindingPeriodStartedAt,
    };
};
